import React, { useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  Button,
  TextField,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  SelectChangeEvent,
} from '@mui/material';

import { useMutation, useQuery } from '@apollo/client';
import { ARCHIVE_GUIDE, GET_GUIDES } from './guide.graphql';
import { GuideStatus, RevisionType } from '../__generated__/graphql';
import { Modal } from '../dialogs/useModal';
import { Guide } from './Details';
import { revisionTypeToDisplayText } from '../revisions/RevisionModel';

export function ArchiveGuideDialog({ modal }: { modal: Modal<Guide, unknown> }) {
  const guide = modal.data;
  const guideId = guide.id;
  const [releaseNotes, setReleaseNotes] = useState('');
  const [replacementGuideIds, setReplacementGuideIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { loading, data } = useQuery(GET_GUIDES);
  const [archiveGuide] = useMutation(ARCHIVE_GUIDE, {
    variables: { input: { id: guideId, releaseNotes, replacementGuides: replacementGuideIds } },
  });
  const ongoingRevision = guide.ongoingRevision;
  const canWithdraw = ongoingRevision?.type === RevisionType.Withdrawal;
  const handleClose = () => {
    modal.close();
  };

  const handleOk = async () => {
    const result = await archiveGuide();
    if (result.data?.guideArchive.errors?.some((e) => e.code === 'NoWithdrawalRevisionForArchiveError')) {
      setError('Kan ikke trekke tilbake anvisningen. Det kreves en pågående revisjon av typen "Tilbaketrekke".');
      return;
    }
    handleClose();
  };

  const availableGuides = !loading
    ? [...(data?.guides?.filter((x) => x!.status !== GuideStatus.Expired) ?? [])].sort((a, b) => (a!.docName ?? '').localeCompare(b!.docName ?? ''))
    : [];

  const handleReplacementGuidesChange = (event: SelectChangeEvent<string[]>) => {
    setReplacementGuideIds(event.target.value as string[]);
  };

  return (
    <Dialog fullWidth={true} maxWidth={'sm'} open={true} onClose={handleClose} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">Trekk tilbake anvisning</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {!canWithdraw && (
            <Alert severity="info">
              Tilbaketrekking krever en pågående revisjon av typen «Tilbaketrekke».{' '}
              {ongoingRevision
                ? `Den pågående revisjonen har typen «${revisionTypeToDisplayText(ongoingRevision.type)}». Endre typen på revisjonen for å låse opp tilbaketrekking.`
                : 'Start en revisjon og sett typen til «Tilbaketrekke» for å låse opp tilbaketrekking.'}
            </Alert>
          )}
          <DialogContentText>
            Dette vil merke anvisningen som tilbaketrukket. Anvisningen vil umiddelbart ikke lenger være en del av aktive anvisninger på web
          </DialogContentText>
          <FormControl fullWidth>
            <InputLabel id="replacement-guides-label">Erstattes av</InputLabel>
            <Select
              labelId="replacement-guides-label"
              multiple
              value={replacementGuideIds}
              onChange={handleReplacementGuidesChange}
              label="Erstattes av"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((id) => {
                    const guide = availableGuides.find((g) => g!.id === id);
                    return <Chip key={id} label={guide ? `${guide.docName} ${guide.docTitle}` : id} size="small" />;
                  })}
                </Box>
              )}>
              {availableGuides.map((guide) => (
                <MenuItem key={guide!.id} value={guide!.id}>
                  {guide!.docName} {guide!.docTitle}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Alert severity="info">Denne teksten vil bli synlig i endringshistorikken for kunde</Alert>
          <TextField
            name="releaseNotes"
            margin="dense"
            label="Årsak til tilbaketrekking av anvisning"
            type="text"
            multiline={true}
            rows={10}
            autoFocus
            value={releaseNotes}
            onChange={(ev) => setReleaseNotes(ev.target.value)}
            fullWidth
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Avbryt</Button>
        <Button onClick={handleOk} disabled={!canWithdraw} variant="contained" color="primary">
          Trekk tilbake
        </Button>
      </DialogActions>
    </Dialog>
  );
}
